/**
 * Data hooks for the Registrations feature (admin-only slice).
 *
 * The reference-data + courier-create hooks used by ≥2 apps were carved out to
 * `@favelafood/shared/registrations/hooks` (C1). This file keeps the admin-only
 * hooks (pending-docs polling, document review, merchant creation) and re-exports
 * the shared ones so existing `@/features/registrations/hooks` consumers keep
 * resolving unchanged.
 *
 * Extended admin DTOs and `/admin/users` aren't in the generated OpenAPI surface
 * yet — those paths go through the shared `callUntyped*` helpers with a cast.
 */
import { useCallback, useEffect } from 'react';
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { api } from '@favelafood/shared/api/client';
import type { components } from '@favelafood/shared/api/generated/api';
import {
  registrationsKeys,
  buildQueryString,
  callUntypedGet,
  callUntypedPost,
  type UserPage,
} from '@favelafood/shared/registrations/hooks';
import type { MerchantAddressInlinePayload } from '@favelafood/shared/registrations/types';
import { useRegistrationsStore } from '@/store/registrations';

/* Re-export the shared registration hooks + types (used by ≥2 apps). */
export {
  registrationsKeys,
  buildQueryString,
  callUntypedGet,
  callUntypedPost,
  useActiveCategories,
  useExistingMerchantOwners,
  useAddressLookup,
  useCreateCourierRegistration,
  type UserPage,
  type UseAddressLookupResult,
} from '@favelafood/shared/registrations/hooks';
export type {
  CreateCourierPayload,
  MerchantAddressInlinePayload,
} from '@favelafood/shared/registrations/types';

type UserResponse = components['schemas']['UserResponse'];
type MerchantResponse = components['schemas']['MerchantResponse'];
type DocumentStatus = components['schemas']['DocumentStatusEnum'];
type BusinessType = components['schemas']['BusinessTypeEnum'];

/* ------------------------------------------------------------------ */
/* Types                                                              */
/* ------------------------------------------------------------------ */

/** Body for `POST /api/v1/admin/merchants`. */
export interface CreateMerchantPayload {
  ownerEmail: string;
  ownerPassword: string;
  ownerName: string;
  ownerPhone?: string | undefined;
  ownerCpf: string;
  ownerFacePhotoUrl?: string | undefined;
  ownerIdentityPhotoUrl?: string | undefined;
  merchantName: string;
  category: string;
  categoryId?: string | undefined;
  description?: string | undefined;
  phone?: string | undefined;
  businessType: BusinessType;
  address: MerchantAddressInlinePayload;
  minimumOrderAmount?: number | undefined;
  estimatedPrepMinutes?: number | undefined;
  approveDocuments?: boolean | undefined;
  autoApprove?: boolean | undefined;
}

/** Body for `POST /api/v1/admin/merchants/for-owner`. */
export interface CreateMerchantForOwnerPayload {
  ownerProfileId: string;
  merchantName: string;
  category: string;
  categoryId?: string | undefined;
  description?: string | undefined;
  phone?: string | undefined;
  businessType: BusinessType;
  address: MerchantAddressInlinePayload;
  minimumOrderAmount?: number | undefined;
  estimatedPrepMinutes?: number | undefined;
  autoApprove?: boolean | undefined;
}

// `StaffCreateMerchantForOwnerPayload` + `useCreateMerchantForOwnerStaff` moved
// to apps/staff (`api/onboarding.ts`) in the 3-app carve (C2): the novo-comércio
// for-owner wizard is staff-only. The admin `useCreateMerchantForOwnerRegistration`
// (its own /admin/merchants/for-owner endpoint) stays below.

export interface UpdateDocumentStatusVars {
  userId: string;
  status: DocumentStatus;
  reason?: string | undefined;
}

/* ------------------------------------------------------------------ */
/* Pending documents (list + count + polling)                          */
/* ------------------------------------------------------------------ */

const PENDING_PAGE_SIZE = 50;

export function usePendingDocsList(size = PENDING_PAGE_SIZE): UseQueryResult<UserPage, Error> {
  return useQuery<UserPage, Error>({
    queryKey: registrationsKeys.pendingDocs(size),
    queryFn: () =>
      // TODO: drop cast once /admin/users is in the OpenAPI doc.
      callUntypedGet<UserPage>(
        `/api/v1/admin/users${buildQueryString({ documentStatus: 'PENDING', size })}`,
      ),
  });
}

const PENDING_POLL_INTERVAL_MS = 60_000;

/**
 * Hook that polls the pending docs counter once per minute and pushes the
 * latest `totalElements` into the Zustand store consumed by the sidebar
 * badge. The hook returns the underlying query so the consumer can show
 * loading / error states or invalidate it manually.
 */
export function useRegistrationsPendingPolling(): UseQueryResult<UserPage, Error> {
  const setPendingDocs = useRegistrationsStore((s) => s.setPendingDocs);

  const query = useQuery<UserPage, Error>({
    queryKey: registrationsKeys.pendingCount(),
    queryFn: () =>
      // TODO: drop cast once /admin/users is in the OpenAPI doc.
      callUntypedGet<UserPage>(
        `/api/v1/admin/users${buildQueryString({ documentStatus: 'PENDING', size: 1 })}`,
      ),
    refetchInterval: PENDING_POLL_INTERVAL_MS,
    refetchIntervalInBackground: false,
  });

  useEffect(() => {
    if (query.data?.totalElements !== undefined) {
      setPendingDocs(query.data.totalElements);
    }
  }, [query.data?.totalElements, setPendingDocs]);

  return query;
}

/* ------------------------------------------------------------------ */
/* Document review                                                     */
/* ------------------------------------------------------------------ */

export interface UseDocumentUrlResult {
  fetchUrl: (userId: string, type: 'face' | 'identity' | 'cpf') => Promise<string>;
}

export function useDocumentUrl(): UseDocumentUrlResult {
  const fetchUrl = useCallback(
    async (userId: string, type: 'face' | 'identity' | 'cpf'): Promise<string> => {
      const path = `/api/v1/admin/users/${encodeURIComponent(userId)}/document-url${buildQueryString({ type })}`;
      // TODO: drop cast once /admin/users/:id/document-url is in the OpenAPI doc.
      const data = await callUntypedGet<{ url?: string }>(path);
      if (!data.url) throw new Error('URL não disponível');
      return data.url;
    },
    [],
  );
  return { fetchUrl };
}

export function useUpdateDocumentStatus(): UseMutationResult<
  UserResponse,
  Error,
  UpdateDocumentStatusVars
> {
  const queryClient = useQueryClient();
  return useMutation<UserResponse, Error, UpdateDocumentStatusVars>({
    mutationFn: async ({ userId, status, reason }) => {
      const body: Record<string, unknown> = { status };
      if (reason && reason.trim().length > 0) body.reason = reason.trim();
      const { data, error } = await api.PATCH('/api/v1/admin/users/{id}/document-status', {
        params: { path: { id: userId } },
        body: body as { status: DocumentStatus },
      });
      if (error) throw error as Error;
      return data as UserResponse;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: registrationsKeys.all });
    },
  });
}

/* ------------------------------------------------------------------ */
/* Create mutations (merchant)                                         */
/* ------------------------------------------------------------------ */

export function useCreateMerchantRegistration(): UseMutationResult<
  MerchantResponse,
  Error,
  CreateMerchantPayload
> {
  const queryClient = useQueryClient();
  return useMutation<MerchantResponse, Error, CreateMerchantPayload>({
    mutationFn: (body) =>
      // TODO: drop cast once the extended AdminCreateMerchantRequest is in the OpenAPI doc.
      callUntypedPost<MerchantResponse>('/api/v1/admin/merchants', body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: registrationsKeys.all });
      void queryClient.invalidateQueries({ queryKey: ['merchants'] });
      void queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });
}

export function useCreateMerchantForOwnerRegistration(): UseMutationResult<
  MerchantResponse,
  Error,
  CreateMerchantForOwnerPayload
> {
  const queryClient = useQueryClient();
  return useMutation<MerchantResponse, Error, CreateMerchantForOwnerPayload>({
    mutationFn: (body) =>
      // TODO: drop cast once the new AdminCreateMerchantForExistingOwnerRequest is in the OpenAPI doc.
      callUntypedPost<MerchantResponse>('/api/v1/admin/merchants/for-owner', body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: registrationsKeys.all });
      void queryClient.invalidateQueries({ queryKey: ['merchants'] });
    },
  });
}

